import { useMediaQuery } from '@mui/material';
import { Container, Item } from 'Components/Grid';
import * as React from 'react';
import { StyledTableControls } from '.';
import { EventStepperContext } from '..';
import colors from '../../../config/theme/colors';

import type { IEventStepper } from '../definitions';
import type { Theme } from '@mui/material';

const TableView = ({
  normalizedProps,
  propsByType,
  types,
  outsideEvents,
  eventLimit,
  tableControlsOverHeader,
}: IOwnProps): React.JSX.Element | null => {
  const eventStepperContext = React.useContext(EventStepperContext);
  const { thisEvents, editingEvent } = eventStepperContext;

  const events = outsideEvents ?? thisEvents;

  const isGroup = propsByType && Array.isArray(types) && types.length > 0;

  const propsToUse = (type?: string): any => {
    if (!isGroup) return normalizedProps;
    return type ? propsByType[type] : propsByType[types[0] as any];
  };

  const { tableContent, tableContentFilter, stepLabelText } = propsToUse();

  // Track page width and breakpoints
  const md = useMediaQuery((theme: Theme) => theme.breakpoints.between(0, 1401));

  // Get suitable event limit displayed in table view based on page width
  const getEventLimit = () => {
    const limit = eventLimit ?? 3;

    if (md) return limit - 1;
    return limit;
  };

  // Used to change the range of events displayed in table view
  const [eventRange, setEventRange] = React.useState<[number, number]>([0, getEventLimit()]);

  const addingNewEvent = editingEvent === true;

  const tableEvents = Array.isArray(events)
    ? (tableContentFilter && tableContentFilter.eventFilter ? tableContentFilter.eventFilter(events) : events).filter(
        (_: any, i: number) =>
          (addingNewEvent && i !== 0) || // Dont show event being created in the list
          !addingNewEvent,
      )
    : [];

  // Set event range to be within limits if page width and therefore event limit changes
  React.useEffect(() => {
    if (Array.isArray(tableEvents)) {
      const diff = getEventLimit() - (eventRange[1] - eventRange[0]);
      if (diff !== 0 && tableEvents.length >= getEventLimit() + 1) {
        if (diff > 0 && eventRange[1] + 1 === tableEvents.length) setEventRange([eventRange[0] - diff, eventRange[1]]);
        else setEventRange([eventRange[0], eventRange[1] + diff]);
      }
    }
  }, [getEventLimit()]);

  React.useEffect(() => {
    // Set the range to the end of the events, when events length changes
    if (Array.isArray(tableEvents)) {
      // Starting range should 0 if theres less events than the limit
      const startRange =
        tableEvents.length - (getEventLimit() + 1) < 0 ? 0 : tableEvents.length - (getEventLimit() + 1);
      setEventRange([startRange, tableEvents.length - 1]);
    }
  }, [Array.isArray(tableEvents) && tableEvents.length]);

  const changeEventRange = (change: number) => (): void => {
    let newRange: React.SetStateAction<[number, number]> = [...eventRange];
    newRange = [newRange[0] + change, newRange[1] + change];
    setEventRange(newRange);
  };

  return tableContent ? (
    <React.Fragment>
      {tableControlsOverHeader && (
        <Container justifyContent={'flex-end'} style={{ width: '100%', margin: '0 0 1rem 0' }}>
          <Item xs={'auto'}>
            {tableEvents.length > 1 && (
              <StyledTableControls
                eventRange={eventRange}
                tableEvents={tableEvents}
                changeEventRange={changeEventRange}
                limit={getEventLimit()}
              />
            )}
          </Item>
        </Container>
      )}
      <Container
        alignItems="center"
        style={{
          backgroundColor: colors.lightGray,
          fontWeight: 600,
          height: '5rem',
          marginBottom: '1rem',
          fontSize: md ? '1.2rem' : '1.6rem',
        }}
      >
        <Item xs={tableContentFilter?.width ?? 3} style={{ paddingLeft: '2rem' }}>
          {tableContentFilter && tableContentFilter.input}
        </Item>
        {tableEvents.slice(eventRange[0], eventRange[1] + 1).map((e: any, i: number, arr: any[]) => (
          <Item key={i} xs={i === arr.length - 1 ? 1 : 2}>
            {stepLabelText(e)}
          </Item>
        ))}
        {!tableControlsOverHeader && (
          <Item xs={true}>
            {tableEvents.length > 1 && (
              <StyledTableControls
                eventRange={eventRange}
                tableEvents={tableEvents}
                changeEventRange={changeEventRange}
                limit={getEventLimit()}
              />
            )}
          </Item>
        )}
      </Container>
      {tableContent(tableEvents.slice(eventRange[0], eventRange[1] + 1))}
    </React.Fragment>
  ) : null;
};

interface IOwnProps {
  normalizedProps: IEventStepper;
  propsByType?: {
    [key: string]: IEventStepper;
  };
  types?: string[];
  /** Bypass context events with these */
  outsideEvents?: any[];
  /** Limit of items to be shown in the table (limit + 1) */
  eventLimit?: number;
  /** Show controls over table headers instead to the right of them */
  tableControlsOverHeader?: boolean;
}

export default TableView;
